import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { generateReferralLink, getReferralEarnings } from '../api/referral'; // Removed generateAffiliateLink from here
import { useNavigate } from 'react-router-dom';
import accountLinkingService from '../services/accountLinkingService';

const GetReferralLink = ({ onLinkGenerated }) => {
  const navigate = useNavigate();
  const { user, affiliateLink: authAffiliateLink, generateAffiliateLink: authGenerateAffiliateLink } = useAuth(); // Get affiliateLink and generateAffiliateLink from AuthContext
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [affiliateLinkState, setAffiliateLinkState] = useState(''); // Renamed to avoid conflict with context
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [affiliateError, setAffiliateError] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [retryCount, setRetryCount] = useState(0);
  const [lastAttempt, setLastAttempt] = useState(null);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [offlineQueue, setOfflineQueue] = useState([]);

  // Earnings state
  const [earnings, setEarnings] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(true);
  const [earningsError, setEarningsError] = useState(null);

  // Account linking state
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [accountVerified, setAccountVerified] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);

  useEffect(() => {
    if (user?.id) {
      generateLink();
      fetchEarnings();
      checkLinkedAccounts();
      // Set affiliate link from AuthContext if available
      if (authAffiliateLink) {
        setAffiliateLinkState(authAffiliateLink);
      }
    } else {
      setError('User ID is not available. Please login to generate referral link.');
      setEarningsLoading(false);
    }
  }, [user, authAffiliateLink]); // Added authAffiliateLink to dependencies

  const fetchEarnings = async () => {
    if (!user?.id) {
      setEarningsError('User not authenticated');
      setEarningsLoading(false);
      return;
    }

    try {
      const earningsData = await getReferralEarnings(user.id);
      if (earningsData) {
        setEarnings(earningsData);
      } else {
        setEarnings({
          totalRevenue: 0,
          userPayout: 0,
          platformCut: 0,
          referralClicks: 0
        });
      }
      setEarningsLoading(false);
    } catch (error) {
      console.error('Error fetching earnings:', error);
      if (error.response?.status === 401) {
        setEarningsError('Authentication failed. Please login again.');
      } else {
        setEarningsError('Failed to load earnings data');
      }
      setEarningsLoading(false);
    }
  };

  const checkLinkedAccounts = async () => {
    if (!user) return;

    setVerificationLoading(true);
    try {
      // Get linked providers from Firebase Auth user
      const linkedProviders = accountLinkingService.getLinkedProviders(user);
      setLinkedAccounts(linkedProviders);

      // Check if user has any linked accounts (Google, etc.)
      const hasLinkedAccounts = linkedProviders.length > 0;
      setAccountVerified(hasLinkedAccounts);

      console.log('Linked accounts check:', { linkedProviders, hasLinkedAccounts });
    } catch (error) {
      console.error('Error checking linked accounts:', error);
      setAccountVerified(false);
    } finally {
      setVerificationLoading(false);
    }
  };

  // Online/Offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const generateLink = async () => {
    setLoading(true);
    setError('');
    try {
      const link = await generateReferralLink(user.id);
      // Ensure the link is a string
      const referralLink = typeof link === 'string' ? link : JSON.stringify(link);
      setReferralLink(referralLink);
      if (onLinkGenerated) {
        onLinkGenerated(referralLink);
      }
    } catch (err) {
      setError('Failed to generate referral link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAffiliateLink = async (retryCount = 0) => {
    const maxRetries = 3;
    const retryDelay = 2000; // 2 seconds

    setAffiliateLoading(true);
    setAffiliateError('');
    setAffiliateLinkState(''); // Clear local state

    if (!user?.id) {
      setAffiliateError('You must register at first.');
      setAffiliateLoading(false);
      return;
    }

    // Check if offline
    if (!isOnline) {
      setAffiliateError('You are currently offline. Your request will be processed when connection is restored.');
      setAffiliateLoading(false);
      return;
    }

    try {
      // Use the generateAffiliateLink from AuthContext
      const response = await authGenerateAffiliateLink(user.id);

      if (response.error === 'USER_NOT_FOUND') {
        setAffiliateError('You must register at first.');
      } else if (response.error === 'OFFLINE_MODE') {
        setAffiliateError(response.message || 'You are currently offline. Your request will be processed when connection is restored.');
      } else if (response.error === 'RATE_LIMIT_EXCEEDED') {
        setAffiliateError('Too many requests. Please wait a moment before trying again.');
      } else if (response.error === 'SERVICE_UNAVAILABLE') {
        setAffiliateError('Service temporarily unavailable. Please try again in a few moments.');
      } else if (response.affiliateLink) {
        setAffiliateLinkState(response.affiliateLink); // Update local state
      } else {
        // Generic error with retry logic for transient failures
        const errorMessage = response.userMessage || response.message || 'Failed to generate affiliate link. Please try again.';
        if (retryCount < maxRetries && (
          response.error === 'GENERATION_FAILED' ||
          response.error === 'FIREBASE_UNAVAILABLE' ||
          !response.error // Network errors
        )) {
          console.log(`Retrying affiliate link generation (attempt ${retryCount + 1}/${maxRetries})`);
          setTimeout(() => handleGenerateAffiliateLink(retryCount + 1), retryDelay);
          return;
        }
        setAffiliateError(errorMessage);
      }
    } catch (err) {
      // Handle network errors with retry
      if (retryCount < maxRetries) {
        console.log(`Network error, retrying affiliate link generation (attempt ${retryCount + 1}/${maxRetries})`);
        setTimeout(() => handleGenerateAffiliateLink(retryCount + 1), retryDelay);
        return;
      }
      setAffiliateError('An error occurred while generating affiliate link. Please check your connection and try again.');
      console.error('Affiliate link generation error:', err);
    } finally {
      setAffiliateLoading(false);
    }
  };

  const copyToClipboard = (link) => {
    if (link) {
      navigator.clipboard.writeText(link);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) return <p>Generating referral link...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <p>Your referral link:</p>
        <input type="text" value={referralLink || ''} readOnly style={{ width: '100%' }} />
      </div>

      {/* Account Verification Status */}
      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '20px' }}>
        <h4>🔗 Account Verification Status</h4>
        {verificationLoading ? (
          <p>Checking account verification...</p>
        ) : accountVerified ? (
          <div>
            <p style={{ color: '#28a745', fontWeight: 'bold' }}>✅ Account Verified</p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              Linked accounts: {linkedAccounts.join(', ')}
            </p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              You can now generate affiliate links.
            </p>
          </div>
        ) : (
          <div>
            <p style={{ color: '#dc3545', fontWeight: 'bold' }}>❌ Account Not Verified</p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              Please link your account credentials (Google, etc.) to generate affiliate links.
            </p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              This helps ensure account security and prevents fraud.
            </p>
          </div>
        )}
      </div>

      <div style={{ marginTop: '30px' }}>
        <h3>🛒 Generate Affiliate Link</h3>
        <button
          onClick={handleGenerateAffiliateLink}
          disabled={affiliateLoading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: affiliateLoading ? 'not-allowed' : 'pointer',
            marginBottom: '15px'
          }}
        >
          {affiliateLoading ? 'Generating...' : 'Generate Affiliate Link'}
        </button>
        {affiliateError && <p style={{ color: 'red' }}>{affiliateError}</p>}
        {(affiliateLinkState || authAffiliateLink) && ( // Use affiliateLinkState or authAffiliateLink
          <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>Your affiliate link:</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <input
                type="text"
                value={affiliateLinkState || authAffiliateLink || ''} // Display from local state or context
                readOnly
                style={{ flex: 1, padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
              />
              <button
                onClick={() => copyToClipboard(affiliateLinkState || authAffiliateLink)} // Copy from local state or context
                style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Copy
              </button>
            </div>
            <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
              Share this link to earn commissions when people sign up and use RapidMind!
            </p>
          </div>
        )}
      </div>

      {/* Earnings Display Section */}
      <div style={{ marginTop: '40px' }}>
        <h3>💰 Referral Earnings</h3>
        {earningsLoading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div>Loading earnings...</div>
          </div>
        ) : earningsError ? (
          <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>
            {earningsError}
          </div>
        ) : earnings ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
              <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                <h4>Total Revenue</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>
                  ${earnings.totalRevenue?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                <h4>Your Earnings (70%)</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>
                  ${earnings.userPayout?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                <h4>Platform Fee (30%)</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#6c757d' }}>
                  ${earnings.platformCut?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                <h4>Total Clicks</h4>
                <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8' }}>
                  {earnings.referralClicks || 0}
                </p>
              </div>
            </div>
            {earnings.userPayout >= 10 && (
              <div style={{ padding: '15px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '8px', color: '#155724' }}>
                🎉 You've reached the minimum payout threshold of $10.00!
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default GetReferralLink;
